import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { PLATFORM_DASHBOARD_TILE } from '../lib/platform-dashboard-layout'
import { useCountUp } from '../hooks/useCountUp'

type MetricValue = number | null | string

interface Props {
  testId?: string
  label: string
  value: MetricValue
  valueFormat?: 'count' | 'percent'
  bg: string
  iconBg: string
  icon: ReactNode
  labelColor?: string
  headerActions?: ReactNode
}

function formatMetricDisplay(
  value: MetricValue,
  valueFormat: 'count' | 'percent',
  animated: number,
): string {
  if (typeof value === 'string') return value
  if (value === null) return 'No data'
  if (valueFormat === 'percent') return `${animated.toFixed(1)}%`
  return animated.toLocaleString()
}

export function PlatformMetricTile({
  testId,
  label,
  value,
  valueFormat = 'count',
  bg,
  iconBg,
  icon,
  labelColor = '#64748b',
  headerActions,
}: Props) {
  const numericTarget = typeof value === 'number' ? value : 0
  const animated = useCountUp(numericTarget, 700, valueFormat === 'percent' ? 1 : 0)
  const display = formatMetricDisplay(value, valueFormat, animated)

  return (
    <Card
      data-testid={testId}
      elevation={0}
      sx={{
        border: `1px solid ${PLATFORM_DASHBOARD_TILE.borderColor}`,
        borderRadius: PLATFORM_DASHBOARD_TILE.borderRadius,
        minHeight: PLATFORM_DASHBOARD_TILE.minHeight,
        bgcolor: bg,
      }}
    >
      <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
            minHeight: PLATFORM_DASHBOARD_TILE.iconSize,
          }}
        >
          <Box
            sx={{
              width: PLATFORM_DASHBOARD_TILE.iconSize,
              height: PLATFORM_DASHBOARD_TILE.iconSize,
              borderRadius: PLATFORM_DASHBOARD_TILE.borderRadius,
              bgcolor: iconBg,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: '#fff',
            }}
          >
            {icon}
          </Box>
          {headerActions}
        </Box>
        <Typography
          component="p"
          sx={{
            fontSize: PLATFORM_DASHBOARD_TILE.valueFontSize,
            fontWeight: 700,
            lineHeight: PLATFORM_DASHBOARD_TILE.valueLineHeight,
            color: '#0f172a',
            mb: 1,
          }}
        >
          {display}
        </Typography>
        <Typography
          sx={{
            fontSize: PLATFORM_DASHBOARD_TILE.labelFontSize,
            lineHeight: PLATFORM_DASHBOARD_TILE.labelLineHeight,
            color: labelColor,
            letterSpacing: '-0.15px',
          }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  )
}
